import { isAssignmentSourceAllowedAtSlot } from '@safetyhub/shared';
import type { AssignmentSource } from '@safetyhub/shared';

interface AssignmentChainEditorProps {
  chain: AssignmentSource[];
  onChange: (chain: AssignmentSource[]) => void;
  disableMemberType?: boolean;
  /**
   * 이 체인이 속한 결재란의 **자리**(0-based). 첫 칸이 아니면 `작성자`·`위임 대상`을 세울 수
   * 없다(S77, 결정 `2026-08-27-author-delegated-sources-first-slot-only.md`).
   *
   * 기본값 0(= 첫 칸)인 이유: 자리를 모르는 호출부에서 제한이 **조용히 걸리는** 것보다
   * 조용히 열리는 편이 낫다. 서버가 어차피 거부하므로 데이터는 지켜지고, 잘못 걸리면
   * 저작자가 고칠 방법이 없는 화면이 된다.
   */
  slotIndex?: number;
  /**
   * 이 칸에 지정할 수 있는 소스 종류(`assigneeSourceWhitelist`). **미지정 = 제한 없음.**
   *
   * 서버(`enforceLockedFromSystem`)는 이 목록을 **하위 층이 보낸 값에만** 적용하고 정의 자신의
   * 체인은 검증하지 않는다. 그래서 이걸 반영하지 않으면 "작성자만 허용"인 칸에 위임 대상을
   * 기본값으로 박을 수 있고, 현장이 손대지 않는 한 그 값이 그대로 쓰여 **제한이 무의미해진다.**
   * 작성 페이지(web `EditModal`)는 이미 허용 밖 선택지를 내놓지 않는다 — 저작 화면도 같은 규약.
   */
  allowedTypes?: AssignmentSource['type'][];
  /** 좁은 패널(Composer)용 compact 레이아웃 — 행을 wrap + full-width로 적층 */
  compact?: boolean;
}

/**
 * 담당자 소스 종류의 한글 라벨. 체인 편집기와 `assigneeSourceWhitelist` 편집기가 **같은 목록을
 * 공유**한다 — 두 곳에 따로 적으면 소스 종류가 늘어날 때 한쪽만 갱신돼 조용히 어긋난다.
 */
export const SOURCE_TYPE_LABELS: Record<AssignmentSource['type'], string> = {
  delegatedTo: '위임 대상',
  role: '역할',
  author: '작성자',
  member: '특정 멤버',
};

/**
 * 역할 코드의 **표시 라벨** — 저장된 값을 읽는 데 쓰므로 전 종류를 담는다.
 * 고를 수 있는 목록은 `SELECTABLE_ROLES`가 따로 정한다.
 *
 * 소스 이름이지 칸 라벨이 아니다. `SAFETY_MANAGER`는 그 현장에 배정된 조직 구성원
 * (안전관리자·대표, PRIMARY 우선)으로 해소되므로 현장 배정 화면 용어인 "점검 담당자"로
 * 부른다(2026-08-31 S14 개정, `ASSIGNMENT_ROLE_LABELS` docblock).
 */
const ROLE_LABELS: Record<string, string> = {
  SAFETY_MANAGER: '점검 담당자',
  OWNER: '책임자(Owner)',
  SITE_CONTACT_BY_SITE: '현장 담당자',
};

/** 역할 select 위에 뜨는 설명 — 누가 그 칸에 들어가는지(그리고 누가 안 들어가는지). */
const ROLE_HELP: Record<string, string> = {
  SAFETY_MANAGER:
    '현장에 배정된 조직 구성원(안전관리자·대표), 주 담당 우선. 계정 없는 현장 담당자는 제외됩니다.',
  OWNER: '현장에 배정된 대표, 없으면 조직의 대표.',
  SITE_CONTACT_BY_SITE: '현장 담당자(계정 없는 인물 포함).',
};

/**
 * **고를 수 있는** 역할 — `SITE_CONTACT_BY_SITE`는 뺐다(2026-08-26 S76-A).
 *
 * `SiteContact`에 주/부 담당 축이 스키마에 없어 "주 담당이 들어간다"는 계약을 지킬 수 없고,
 * 저장 실적도 0건이었다. **enum 값과 서버 해소기는 남긴다** — 공유 union 변경은 파급이 크고,
 * 남아 있을 수 있는 저장분을 읽지 못하게 되는 편이 더 나쁘다.
 * ⚠️ 주/부 축이 생기면 이 목록에 되살린다.
 */
const SELECTABLE_ROLES = ['SAFETY_MANAGER', 'OWNER'] as const;

/**
 * 화면에 내놓는 순서 — **첫 결재란 전용 소스(위임 대상·작성자)를 앞으로 몰아둔다**(S77).
 *
 * 둘째 칸부터는 그 둘이 통째로 비활성이라, 섞어 놓으면 활성·비활성이 번갈아 나와 목록이
 * 읽기 어려워진다. 앞으로 모으면 *"여기부터는 못 고른다"* 는 경계가 한눈에 보인다.
 * ⚠️ 이 배열은 **표시 순서**일 뿐 판정이 아니다 — 판정은 `isAssignmentSourceAllowedAtSlot`
 * (`@safetyhub/shared`)가 하고 서버도 같은 함수를 쓴다.
 */
export const SOURCE_TYPES = ['delegatedTo', 'author', 'role', 'member'] as const;

/**
 * 소스 종류를 고르면 만들어지는 기본 값. 체인 편집기의 종류 select와 "담당자 지정" 버튼
 * (미지정 칸을 지정 상태로 전환)이 공유한다.
 */
export function makeDefaultSource(type: string): AssignmentSource {
  if (type === 'role') return { type: 'role', role: 'SAFETY_MANAGER' };
  if (type === 'member') return { type: 'member', memberId: '' };
  if (type === 'author') return { type: 'author' };
  return { type: 'delegatedTo' };
}

/**
 * 이 편집기가 체인에 실제로 넣을 수 있는 종류 — whitelist·`disableMemberType`·**자리(S77)**의
 * 교집합.
 *
 * **셋은 축이 다르다.** whitelist(`assigneeSourceWhitelist`)는 "이 칸에 누구를 세울 수 있나"로
 * 하위 층(현장·작성자)까지 향하는 제한이고, `disableMemberType`은 "이 편집기에서 특정 개인을
 * 박을 수 있나"라는 저작 층만의 제약이다(양식이 memberId를 박으면 조직·현장이 바뀔 때 깨진다).
 * `slotIndex`는 **결재 순서상의 자리**가 정하는 제한이다 — 첫 칸이 아니면 `작성자`·`위임 대상`이
 * 결재를 만들지 않고 자동으로 채워지기만 하므로 세울 수 없다(S77).
 *
 * 그래서 `특정 멤버`만 허용된 칸은 whitelist로는 완전히 유효하지만 — 작성자가 조직 멤버를
 * 골라 세우는 흔한 구성이다 — **양식이 직접 세울 수는 없다.** 그 경우 이 목록이 비고,
 * 호출부는 "여기서는 세울 수 없다"고 알린다.
 */
export function chainAssignableTypes(
  allowedTypes: AssignmentSource['type'][] | undefined,
  disableMemberType = false,
  slotIndex = 0,
): AssignmentSource['type'][] {
  return SOURCE_TYPES.filter(
    (t) =>
      (!allowedTypes || allowedTypes.includes(t)) &&
      !(t === 'member' && disableMemberType) &&
      isAssignmentSourceAllowedAtSlot(t, slotIndex),
  );
}

/**
 * 새 소스를 만들 때 고를 기본 종류 — 넣을 수 있는 종류가 하나도 없으면 `undefined`.
 *
 * 체인의 "+ 소스 추가"와 미지정 칸의 "+ 담당자 지정"이 공유한다. 두 곳이 따로 판단하면
 * 한쪽만 제한을 따르는 어긋남이 생긴다(실제로 "담당자 지정"만 whitelist를 보고 소스 추가는
 * 무조건 위임 대상을 넣던 상태였다).
 */
export function firstAllowedSourceType(
  allowedTypes: AssignmentSource['type'][] | undefined,
  disableMemberType = false,
  slotIndex = 0,
): AssignmentSource['type'] | undefined {
  return chainAssignableTypes(allowedTypes, disableMemberType, slotIndex)[0];
}

export default function AssignmentChainEditor({
  chain,
  onChange,
  disableMemberType = false,
  allowedTypes,
  compact = false,
  slotIndex = 0,
}: AssignmentChainEditorProps) {
  // 이 편집기에서 넣을 수 있는 종류. 비어 있으면(예: `특정 멤버`만 허용된 양식 칸) 추가
  // 자체가 불가능하므로 버튼을 내리고, 대신 어디서 지정되는지 알린다.
  const assignableTypes = chainAssignableTypes(allowedTypes, disableMemberType, slotIndex);

  const add = () => {
    const type = assignableTypes[0];
    if (!type) return;
    onChange([...chain, makeDefaultSource(type)]);
  };

  const remove = (idx: number) => {
    onChange(chain.filter((_, i) => i !== idx));
  };

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...chain];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };

  const moveDown = (idx: number) => {
    if (idx === chain.length - 1) return;
    const next = [...chain];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  };

  const updateType = (idx: number, type: string) => {
    const next = [...chain];
    next[idx] = makeDefaultSource(type);
    onChange(next);
  };

  const updateRole = (idx: number, role: string) => {
    const next = [...chain];
    next[idx] = { type: 'role', role: role as 'SAFETY_MANAGER' | 'OWNER' | 'SITE_CONTACT_BY_SITE' };
    onChange(next);
  };

  const updateMemberId = (idx: number, memberId: string) => {
    const next = [...chain];
    next[idx] = { type: 'member', memberId };
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {chain.map((src, idx) => (
        <div
          key={idx}
          className={
            compact
              ? 'flex flex-wrap items-center gap-2 bg-gray-50 rounded p-2'
              : 'flex items-center gap-2 bg-gray-50 rounded p-2'
          }
        >
          <span className="text-xs text-gray-400 w-4 shrink-0">{idx + 1}</span>
          <select
            value={src.type}
            onChange={(e) => updateType(idx, e.target.value)}
            className={
              compact
                ? 'border border-gray-300 rounded px-2 py-1 text-xs w-full min-w-0'
                : 'border border-gray-300 rounded px-2 py-1 text-xs'
            }
          >
            {SOURCE_TYPES.map((t) => {
              // 고를 수 없는 종류는 목록에서 지우지 않고 **비활성**으로 남긴다 — 이미 그 값이
              // 들어 있는 기존 칸(나중에 whitelist를 좁힌 경우)에서 현재 선택이 사라지면
              // select가 엉뚱한 값을 가리킨다.
              //
              // 막히는 이유는 셋이고 **성격이 다르다.** 사유 없이 회색으로만 두면
              // "④에서 허용했는데 왜 못 고르지"가 된다.
              //
              // ⚠️ **사유는 셋 다 라벨에 적는다 — `title`은 보완이지 전달 수단이 아니다.**
              // Chrome은 `<option title>` 툴팁을 렌더하지 않아, 호버로만 준 사유는 그
              // 브라우저에서 **어디에도 보이지 않는다**(종전 S77이 그랬다). `title`은 그래도
              // 남겨 둔다 — 렌더하는 브라우저에서는 라벨보다 긴 설명을 줄 수 있다.
              //
              // 길이만 사유별로 다르게 둔다:
              //  - `허용 안 됨`(whitelist)·`보고서에서 지정`(저작 층 제약) → 양식 설정에 따라
              //    달라지고 **뜻밖이라** 눈에 보여야 한다.
              //  - `첫 칸만`(S77) → 2번 칸부터는 **늘 같은 둘이 회색**이라 구조적으로 뻔하고
              //    매 행에 두 번씩 반복되므로, 목록이 사유 텍스트로 뒤덮이지 않게 가장 짧게.
              const notAllowed = allowedTypes ? !allowedTypes.includes(t) : false;
              const authoringBlocked = t === 'member' && disableMemberType;
              // S77: 첫 칸이 아니면 `작성자`·`위임 대상`을 세울 수 없다. 뒤 칸에 두면 작성·제출과
              // 동시에 자동으로 채워져 제3자 결재를 한 번도 거치지 않기 때문이다.
              const slotBlocked = !isAssignmentSourceAllowedAtSlot(t, slotIndex);
              const inlineReason = notAllowed
                ? ' (허용 안 됨)'
                : authoringBlocked
                  ? ' (보고서에서 지정)'
                  : slotBlocked
                    ? ' (첫 칸만)'
                    : '';
              const hoverReason = notAllowed
                ? '이 칸에 허용된 담당자 소스가 아닙니다(양식이 정한 제한).'
                : authoringBlocked
                  ? '양식에서는 특정 인물을 박지 않습니다 — 보고서·현장에서 지정합니다.'
                  : slotBlocked
                    ? '첫 결재란에서만 고를 수 있습니다 — 뒤 칸에 두면 작성·제출과 동시에 채워져 제3자 결재를 거치지 않습니다.'
                    : undefined;
              return (
                <option
                  key={t}
                  value={t}
                  disabled={notAllowed || authoringBlocked || slotBlocked}
                  title={hoverReason}
                >
                  {SOURCE_TYPE_LABELS[t]}
                  {inlineReason}
                </option>
              );
            })}
          </select>

          {src.type === 'role' && (
            <select
              value={src.role}
              title={ROLE_HELP[src.role]}
              onChange={(e) => updateRole(idx, e.target.value)}
              className={
                compact
                  ? 'border border-gray-300 rounded px-2 py-1 text-xs w-full min-w-0'
                  : 'border border-gray-300 rounded px-2 py-1 text-xs'
              }
            >
              {/* 이미 저장된 값이 감춰진 역할이면 그 항목만 살려 둔다 — 아니면 select가
                  빈칸으로 보여 "지정이 사라진 것"처럼 읽힌다. */}
              {(SELECTABLE_ROLES.includes(src.role as (typeof SELECTABLE_ROLES)[number])
                ? [...SELECTABLE_ROLES]
                : [...SELECTABLE_ROLES, src.role]
              ).map((k) => (
                <option key={k} value={k}>{ROLE_LABELS[k] ?? k}</option>
              ))}
            </select>
          )}

          {src.type === 'member' && (
            <input
              type="text"
              value={src.memberId}
              onChange={(e) => updateMemberId(idx, e.target.value)}
              placeholder="Member ID"
              className={
                compact
                  ? 'border border-gray-300 rounded px-2 py-1 text-xs w-full min-w-0'
                  : 'border border-gray-300 rounded px-2 py-1 text-xs flex-1'
              }
            />
          )}

          {(src.type === 'delegatedTo' || src.type === 'author') && (
            <span className={compact ? 'text-xs text-gray-500 w-full' : 'text-xs text-gray-500 flex-1'}>
              {src.type === 'delegatedTo' ? '보고서 생성 시 위임 지정' : '보고서 작성자 자동'}
            </span>
          )}

          <div className="flex gap-1 ml-auto shrink-0">
            <button
              type="button"
              onClick={() => moveUp(idx)}
              disabled={idx === 0}
              className="px-1 py-0.5 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => moveDown(idx)}
              disabled={idx === chain.length - 1}
              className="px-1 py-0.5 text-xs text-gray-500 hover:text-gray-700 disabled:opacity-30"
            >
              ↓
            </button>
            <button
              type="button"
              onClick={() => remove(idx)}
              className="px-1 py-0.5 text-xs text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </div>
        </div>
      ))}
      {assignableTypes.length > 0 ? (
        <button
          type="button"
          onClick={add}
          className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
        >
          + 소스 추가
        </button>
      ) : (
        <p className="text-xs text-gray-500">
          허용된 소스가 양식에서 세울 수 없는 종류뿐입니다 — 담당자는 보고서를 만들 때
          지정합니다.
        </p>
      )}
    </div>
  );
}
